import React from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { Picker } from "react-native";
import { Modal, Text, TextInput, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { addTrackerOrderEnquiry } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import Spinner from "../../../components/shared/Spinner";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import { DeviceOrientation } from "../../../components/context/utils/DeviceOrientation";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

const TrackerEnquiryModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const dispatch = useDispatch<any>();
  const { loadingAddEnqiry } = useSelector((state: any) => state.chinaTracker);
  const toast = useToast();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: any) => {
    const payload = {
      orderNumber: order.OrderNumber1 ? order.OrderNumber1 : order.PODoc,
      customerName: order.BPName,
      itemCode: order.ItemCode,
      itemName: order.ItemName,
      orderDate: order.OrderDate,
      subject: data.subject,
      priority: data.priority,
      message: data.message,
      key: order.Key,
      enquiryTrackerType: "CHINA_TRACKER",
    };
    dispatch(addTrackerOrderEnquiry(payload))
      .unwrap()
      .then(() =>
        toast.show("Enquiry created successfully", {
          type: "success",
        })
      )
      .catch((err: any) =>
        toast.show(err.message || err || "Error creating enquiry", {
          type: "danger",
        })
      );

    onClose();
  };

  const { isLargeDevice } = DeviceOrientation();
  const isDesktop = isLargeDevice;

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.modalContainer}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>{t("askEnquiry")}</Text>
              <Text style={styles.subtitle}>
                {translateFields("OrderNumber1", t, chinaTrackerTranslationMap)}: {order?.OrderNumber1}
              </Text>
            </View>

            {loadingAddEnqiry ? (
              <Spinner
                size="small"
                color="#61B8FF"
                styles={{
                  flexDirection: "row",
                  marginTop: "5%",
                  marginBottom: "5%",
                }}
              />
            ) : (
              <>
                {/* Order Details Card */}
                <View style={styles.detailsCard}>
                  <View style={[styles.detailsGrid, isDesktop && styles.detailsGridDesktop]}>
                    <View style={[styles.detailItem, isDesktop && { flex: 1 }]}>
                      <Text style={styles.detailLabel}>
                        {translateFields("Item", t, chinaTrackerTranslationMap)}
                      </Text>
                      <Text style={styles.detailValue} numberOfLines={2}>
                        {order?.ItemCode} {order?.ItemName}
                      </Text>
                    </View>
                    <View style={[styles.detailItem, isDesktop && { flex: 1 }]}>
                      <Text style={styles.detailLabel}>
                        {translateFields("Customer", t, chinaTrackerTranslationMap)}
                      </Text>
                      <Text style={styles.detailValue}>{order?.CustomerRef}</Text>
                    </View>
                    <View style={[styles.detailItem, isDesktop && { flex: 1 }]}>
                      <Text style={styles.detailLabel}>
                        {translateFields("OrderPlaced", t, chinaTrackerTranslationMap)}
                      </Text>
                      <Text style={styles.detailValue}>{order?.OrderDate}</Text>
                    </View>
                  </View>
                </View>

                {/* Form Fields */}
                <View style={[styles.formRow, isDesktop && styles.formRowDesktop]}>
                  <View style={[styles.fieldContainer, isDesktop && { flex: 1, marginRight: 12 }]}>
                    <Text style={styles.fieldLabel}>
                      {translateFields("Subject", t, chinaTrackerTranslationMap)}
                    </Text>
                    <Controller
                      control={control}
                      name="subject"
                      rules={{ required: "Subject is required" }}
                      render={({ field: { onChange, value } }) => (
                        <>
                          <Picker
                            selectedValue={value}
                            onValueChange={onChange}
                            style={styles.picker}
                          >
                            <Picker.Item
                              label={translateFields("SelectSubject", t, chinaTrackerTranslationMap)}
                              value=""
                            />
                            <Picker.Item
                              label={translateFields("OrderIssue", t, chinaTrackerTranslationMap)}
                              value="Order Issue"
                            />
                            <Picker.Item
                              label={translateFields("ProductInquiry", t, chinaTrackerTranslationMap)}
                              value="Product Inquiry"
                            />
                            <Picker.Item
                              label={translateFields("Other", t, chinaTrackerTranslationMap)}
                              value="Other"
                            />
                          </Picker>
                          {errors.subject && (
                            <Text style={styles.errorText}>
                              {errors.subject.message as string}
                            </Text>
                          )}
                        </>
                      )}
                    />
                  </View>

                  <View style={[styles.fieldContainer, isDesktop && { flex: 1 }]}>
                    <Text style={styles.fieldLabel}>
                      {translateFields("Priority", t, chinaTrackerTranslationMap)}
                    </Text>
                    <Controller
                      control={control}
                      name="priority"
                      rules={{ required: "Priority is required" }}
                      render={({ field: { onChange, value } }) => (
                        <>
                          <Picker
                            selectedValue={value}
                            onValueChange={onChange}
                            style={styles.picker}
                          >
                            <Picker.Item
                              label={translateFields("SelectPriority", t, chinaTrackerTranslationMap)}
                              value=""
                            />
                            <Picker.Item
                              label={translateFields("Low", t, chinaTrackerTranslationMap)}
                              value="Low"
                            />
                            <Picker.Item
                              label={translateFields("High", t, chinaTrackerTranslationMap)}
                              value="High"
                            />
                          </Picker>
                          {errors.priority && (
                            <Text style={styles.errorText}>
                              {errors.priority.message as string}
                            </Text>
                          )}
                        </>
                      )}
                    />
                  </View>
                </View>

                {/* Message */}
                <View style={styles.fieldContainer}>
                  <Text style={styles.fieldLabel}>
                    {translateFields("Message", t, chinaTrackerTranslationMap)}
                  </Text>
                  <Controller
                    control={control}
                    name="message"
                    rules={{ required: "Message is required" }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <TextInput
                          style={[styles.messageInput, errors.message && styles.messageInputError]}
                          multiline
                          placeholder={translateFields("TypeYourMessage", t, chinaTrackerTranslationMap)}
                          placeholderTextColor="#9CA3AF"
                          value={value}
                          onChangeText={onChange}
                        />
                        {errors.message && (
                          <Text style={styles.errorText}>
                            {errors.message.message as string}
                          </Text>
                        )}
                      </>
                    )}
                  />
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                  <TouchableOpacity
                    onPress={onClose}
                    style={styles.closeButton}
                  >
                    <Text style={styles.closeButtonText}>{t("close")}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.submitButton}
                    onPress={handleSubmit(onSubmit)}
                  >
                    <Text style={styles.submitText}>{t("submit")}</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const { width } = Dimensions.get("window");
const isDesktopWidth = width > 760;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 20,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: isDesktopWidth ? 32 : 20,
    width: isDesktopWidth ? "86%" : "92%",
    maxWidth: isDesktopWidth ? 900 : 500,
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 12,
  },

  /* Header */
  header: {
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingBottom: 16,
    marginBottom: 16,
  },
  title: {
    fontSize: isDesktopWidth ? 22 : 18,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: "#6B7280",
    fontWeight: "500",
  },

  /* Order Details Card */
  detailsCard: {
    backgroundColor: "#F8FAFC",
    borderRadius: 10,
    padding: isDesktopWidth ? 16 : 12,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    marginBottom: 20,
  },
  detailsGrid: {
    flexDirection: "column",
  },
  detailsGridDesktop: {
    flexDirection: "row",
    gap: 16,
  },
  detailItem: {
    marginBottom: isDesktopWidth ? 0 : 10,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#94A3B8",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 3,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: "500",
    color: "#1E293B",
  },

  /* Form Fields */
  formRow: {
    flexDirection: "column",
  },
  formRowDesktop: {
    flexDirection: "row",
  },
  fieldContainer: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 6,
  },
  picker: {
    height: 42,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
  },
  messageInput: {
    height: 100,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#fff",
    fontSize: 14,
    color: "#111827",
    textAlignVertical: "top",
  },
  messageInputError: {
    borderColor: "#EF4444",
  },
  errorText: {
    color: "#EF4444",
    fontSize: 12,
    marginTop: 4,
  },

  /* Footer */
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 8,
    gap: 10,
  },
  closeButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  closeButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
  },
  submitButton: {
    backgroundColor: "#2563EB",
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
  },
  submitText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 14,
  },
});

export default TrackerEnquiryModal;
